/** How long a touch-driven scroller has to stay still before its position may be written.
 *
 *  iOS decelerates toward an offset UIKit fixed when the finger lifted (`targetContentOffset`), so a
 *  `scrollTop` the page writes while the finger is down or the deceleration runs is overridden by the
 *  next animation tick. Prepending history needs exactly such a write to hold the reader's place, so
 *  the prepend waits until the gesture and its momentum have ended. Mouse, keyboard and scrollbar
 *  scrolling never start a touch session and never wait. */
const SCROLL_SETTLE_MS = 250;

export interface ScrollSettleGate {
  /** A `touchstart` on the scroller. */
  touchStart(): void;
  /** A `touchend` or `touchcancel`. Momentum may follow; `scrolled` keeps the session open. */
  touchEnd(): void;
  /** A reader-driven `scroll` event. Counts only inside a touch session. */
  scrolled(): void;
  /** Resolves at once outside a touch session; otherwise once the finger is up and the scroller has not
   *  moved for `settleMs`. */
  whenSettled(): Promise<void>;
  /** The scroller is gone: stop the timer and release every waiter, so a page held for it still commits. */
  dispose(): void;
}

export function createScrollSettleGate(settleMs = SCROLL_SETTLE_MS, now: () => number = () => performance.now()): ScrollSettleGate {
  let touching = false;
  let session = false;
  let lastMotionAt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let waiters: (() => void)[] = [];

  const settled = (): boolean => !session || (!touching && now() - lastMotionAt >= settleMs);
  const release = (): void => {
    const pending = waiters;
    waiters = [];
    for (const resolve of pending) resolve();
  };
  const check = (): void => {
    timer = null;
    if (!settled()) { arm(); return; }
    session = false;
    release();
  };
  const arm = (): void => {
    if (timer !== null || touching || !session) return;
    timer = setTimeout(check, Math.max(0, lastMotionAt + settleMs - now()));
  };
  const clear = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  return {
    touchStart() {
      touching = true;
      session = true;
      lastMotionAt = now();
      clear();
    },
    touchEnd() {
      touching = false;
      lastMotionAt = now();
      arm();
    },
    scrolled() {
      if (!session) return;
      lastMotionAt = now();
      clear();
      arm();
    },
    whenSettled() {
      if (settled()) { session = false; return Promise.resolve(); }
      return new Promise<void>((resolve) => { waiters.push(resolve); arm(); });
    },
    dispose() {
      clear();
      session = false;
      release();
    },
  };
}
